<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <LocationSelector
    v-if="locationModalOpen"
    :locations="locations"
    :location-modal-open="locationModalOpen"
    :selected-locations="[...getSelectedTreeLocations]"
    @modal-close="locationModalOpen = false"
    @update-locations="applySectedLocations"
  />
  <div class="m-auto flex h-full min-h-[95vh] max-w-screen-xl flex-col">
    <div class="py-4">
      <Icon
        name="ph:house-line-fill"
        class="mr-1 inline-block size-6 cursor-pointer"
        @click="goHome"
      />
      <span class="align-top"> - Reports</span>
    </div>

    <div class="border-muted-200 dark:border-muted-700 dark:bg-muted-800 flex grow border bg-white">
      <div class="m-4 max-w-full grow">
        <div class="flex h-full gap-4">
          <!-- Left fieldset - full height -->
          <fieldset
            class="border-muted-200 dark:border-muted-700 dark:bg-muted-800 row-span-3 w-1/5 min-w-72 rounded-md border bg-white"
          >
            <div data-v-4db1fa6d="" class="border-muted-200 bg-muted-50 dark:border-muted-700 dark:bg-muted-800/50 h-[65px] border-b px-4 py-3">
              <h2 data-v-4db1fa6d="" class="text-muted-800 dark:text-muted-100 pt-2 font-semibold">
                Report Categories
              </h2>
            </div>
            <BaseTreeSelect
              ref="treeSelectRef"
              class="overflow-x-hidden p-4 text-left"
              :children="reportTreeViewData"
            >
              <template #item-label="{ level, child, toggle }">
                <BaseTreeSelectItem
                  v-model="selectedReport"
                  :level="level"
                  :toggle="toggle"
                  :value="{
                    name: child.item,
                  }"
                  @click="itemSelected()"
                />
              </template>
            </BaseTreeSelect>
          </fieldset>
          <!-- Main content area - fills remaining space -->
          <div class="grid-col-3 col-span-3 flex grow flex-col gap-4">
            <!-- Bottom-right fieldset - fills remaining space -->
            <fieldset
              v-if="selectedReport"
              class="border-muted-200 dark:border-muted-700 dark:bg-muted-800 col-span-3 flex grow flex-col gap-4 rounded-md border bg-white"
            >
              <div class="border-muted-200 bg-muted-50 dark:border-muted-700 dark:bg-muted-800/50 flex items-center justify-between border-b px-4 py-3">
                <div class="text-muted-800 dark:text-muted-100 max-h-[65px] grow font-semibold ">
                  <h1>{{ selectedReport.ReportName }}</h1>
                </div>
                <div v-if="table" class="mr-3">
                  <BaseDropdown
                    variant="button"
                    label="Download"
                    placement="bottom-start"
                    size="md"
                    class="inline-block"
                  >
                    <BaseDropdownItem
                      :title="t('reporting.csv')"
                      rounded="sm"
                      @click="table.download('csv', (selectedReport?.ReportExecuteName || 'report') + '.csv')"
                    />
                    <!--
                  <BaseDropdownItem
                    :title="t('reporting.xlsx')"
                    rounded="sm"
                    @click="table.download('xlsx', (selectedReport?.ReportExecuteName || 'report') + '.xlsx')"
                  />
-->
                    <BaseDropdownItem
                      :title="t('reporting.pdf')"
                      rounded="sm"
                      @click="table.download('pdf', (selectedReport?.ReportExecuteName || 'report') + '.pdf')"
                    />

                    <BaseDropdownItem
                      :title="t('reporting.html')"
                      rounded="sm"
                      @click="table.download('html', (selectedReport?.ReportExecuteName || 'report') + '.html')"
                    />
                    <BaseDropdownItem
                      :title="t('reporting.json')"
                      rounded="sm"
                      @click="table.download('json', (selectedReport?.ReportExecuteName || 'report') + '.json')"
                    />
                  </BaseDropdown>
                  <BaseButton
                    class="ml-3 inline-block"
                    :disabled="selectedReport == undefined"
                    @click="table?.print()"
                  >
                    {{ t('reporting.print') }}
                  </BaseButton>
                </div>
                <BaseButton
                  tooltip="Filter Report"
                  @click="toggleDrawer"
                >
                  <Icon name="mdi:filter" class="size-4 text-black dark:text-black" />
                </BaseButton>
              </div>
              <div class="grow p-4 pt-0">
                <div id="reports-result" class="h-full" /> <!-- Results content -->
              </div>
            </fieldset>
            <!-- Empty state when no report selected -->
            <div
              v-else
              class="border-muted-300 bg-muted-50 dark:border-muted-700 dark:bg-muted-800/40 flex grow flex-col items-center justify-center rounded-xl border border-dashed p-8"
            >
              <Icon name="mdi:file-document-outline" class="text-muted-400 mb-4 size-16" />
              <h2 class="text-muted-800 dark:text-muted-200 mb-2 text-xl font-medium">
                No Report Selected
              </h2>
              <p class="text-muted-500 dark:text-muted-400 text-center">
                Please select a report from the list to view results
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Side Drawer Component -->
    <Teleport to="body">
      <div v-if="isDrawerOpen" class="fixed inset-0 z-50 flex">
        <!-- Backdrop -->
        <div
          class="bg-muted-900/60 fixed inset-0"
          role="button"
          tabindex="0"
          aria-label="Close drawer"
          @click="toggleDrawer"
        />

        <!-- Drawer panel -->
        <div class="dark:bg-muted-800 fixed right-0 z-50 flex h-full w-1/2 flex-col bg-white shadow-lg">
          <div class="border-muted-200 dark:border-muted-700 flex items-center justify-between border-b p-4">
            <h3 class="text-lg font-medium">
              Filter Report
            </h3>
            <BaseButton
              shape="rounded"
              class="border-muted-200 dark:border-muted-600 border p-0 shadow-sm"
              @click="toggleDrawer"
            >
              <Icon name="mdi:close" class="size-4 text-black dark:text-black" />
            </BaseButton>
          </div>
          <div class="flex-1 overflow-auto p-4">
            <!-- Report filter content -->
            <div v-if="selectedReport" class="space-y-6">
              <div class="space-y-6">
                <!-- Text input filters - 2 columns -->
                <div v-if="selectedReport?.Filters.some(a => a.type == 'textInput')">
                  <div class="grid grid-cols-2 gap-6">
                    <BaseInputCaps
                      v-for="(f) in selectedReport?.Filters.filter(a => (a.type == 'textInput'))"
                      :id="'filter_input_' + f.fieldName"
                      :key="f.label"
                      v-model="textFilters[f.fieldName]"
                      :label="f.label"
                    />
                  </div>
                </div>
                <div v-if="selectedReport?.Filters.some(a => a.type == 'date')">
                  <div class="grid grid-cols-2 gap-6">
                    <BaseInputCaps
                      v-for="(f) in selectedReport?.Filters.filter(a => (a.type == 'date'))"
                      :id="'filter_input_' + f.fieldName"
                      :key="f.label"
                      v-model="dateFilters[f.fieldName]"
                      type="date"
                      :label="f.label"
                    />
                  </div>
                </div>
                <div v-if="selectedReport?.Filters.some(a => a.type == 'boolean')">
                  <div class="grid grid-cols-2 gap-6">
                    <BaseCheckbox
                      v-for="(f) in selectedReport?.Filters.filter(a => (a.type == 'boolean'))"
                      :id="'filter_input_' + f.fieldName"
                      :key="f.label"
                      v-model="boolFilters[f.fieldName]"
                      :label="f.label"
                    />
                  </div>
                </div>
                <div v-if="selectedReport?.Filters.some(a => a.type == 'select')">
                  <div class="grid grid-cols-2 gap-6">
                    <BaseSelect
                      v-for="(f) in selectedReport?.Filters.filter(a => (a.type == 'select'))"
                      :id="'filter_input_' + f.fieldName"
                      :key="f.label"
                      v-model="selectFilters[f.fieldName]"
                      :label="f.label"
                      :items="f.items"
                    >
                      <option
                        v-for="(o) in f.items"
                        :key="o.value"
                        :value="o.value"
                      >
                        {{ o.label }}
                      </option>
                    </BaseSelect>
                  </div>
                </div>

                <!-- Visit types section -->
                <div
                  v-if="selectedReport.Filters.some(a => a.type == 'visitUserTypes')"
                  class="dark:border-muted-700  pt-4"
                >
                  <BaseListbox
                    v-model="selectedVisitTypes"
                    label="Select Visit Types"
                    :multiple="true"
                    :multiple-label="(values: VisitUserType[]) => {
                      return values.map((a: VisitUserType) => a.description).join(', ')
                    }"
                    :items="userVisitTypes"
                    :properties="{
                      value: 'visitUserTypeId',
                      label: 'description'
                    }"
                  />
                </div>

                <!-- CustomField Types section -->
                <div
                  v-if="selectedReport.Filters.some(a => a.type == 'customFieldTypes')"
                  class="dark:border-muted-700  pt-4"
                >
                  <BaseListbox
                    v-model="selectedCustomFields"
                    label="Select Custom Fields"
                    :multiple="true"
                    :multiple-label="(values: CustomFieldType[]) => {
                      return values.map((a: CustomFieldType) => a.dataCaption).join(', ')
                    }"
                    :items="userCustomFields"
                    :properties="{
                      value: 'customFieldTypeId',
                      label: 'dataCaption'
                    }"
                  />
                </div>

                <!-- CustomField Expiry Types section -->
                <div
                  v-if="selectedReport.Filters.some(a => a.type == 'customExpiryFieldTypes')"
                  class="dark:border-muted-700  pt-4"
                >
                  <BaseListbox
                    v-model="selectedCustomExpiryFields"
                    label="Select Custom Expiry Fields"
                    :multiple="true"
                    :multiple-label="(values: CustomFieldType[]) => {
                      return values.map((a: CustomFieldType) => a.dataCaption).join(', ')
                    }"
                    :items="userCustomExpiryFields"
                    :properties="{
                      value: 'customFieldTypeId',
                      label: 'dataCaption'
                    }"
                  />
                </div>

                <div
                  v-if="selectedReport.Filters.some(a => a.type == 'issuableItemCategory')"
                  class="dark:border-muted-700  "
                >
                  <BaseListbox
                    v-model="selectedIssuableItemCategories"
                    label="Select Issuable Item Category"
                    :multiple="true"
                    :multiple-label="(values: IssuableItemCategory[]) => {
                      return values.map((a: IssuableItemCategory) => a.description).join(', ')
                    }"
                    :items="IssuableItemCategories"
                    :properties="{
                      value: 'issuableItemCategoryId',
                      label: 'description'
                    }"
                  />
                </div>

                <!-- Locations section -->
                <div class="border-muted-200 dark:border-muted-700 border-t pt-4">
                  <h5 class="mb-4 text-lg font-medium">
                    Locations
                  </h5>

                  <div class="bg-muted-100 dark:bg-muted-700/30 mb-4 rounded-md p-3">
                    <div v-if="selectedLocations.length > 0" class="flex flex-wrap gap-2">
                      <div
                        v-for="location in selectedLocations"
                        :key="location.locationId"
                        class="dark:bg-muted-800 flex items-center gap-1 rounded bg-white px-2 py-1 text-xs"
                      >
                        <Icon name="lucide:map-pin" class="size-3" />
                        <span>{{ location.description }}</span>
                      </div>
                    </div>
                    <p v-else class="text-muted-500 dark:text-muted-400 text-sm">
                      No locations selected
                    </p>
                  </div>

                  <BaseButton
                    class="w-full"
                    :disabled="selectedReport == undefined || busy"
                    :loading="busy"
                    @click="locationModalOpen = true"
                  >
                    <Icon name="lucide:map-pin" class="mr-1 size-4" />
                    {{ t('reporting.locations') || 'Select Locations' }}
                  </BaseButton>
                </div>
              </div>

              <div
                class="border-muted-200 dark:border-muted-700 align-right flex-direction-row-reverse flex gap-4 border-t pt-6"
              >
                <BaseButton
                  class="max-w-fit grow"
                  color="primary"
                  :disabled="selectedReport == undefined || busy"
                  :loading="busy"
                  @click="generateReport();"
                >
                  <Icon name="lucide:play" class="mr-2 size-4" />
                  {{ t('reporting.generate') || 'Generate Report' }}
                </BaseButton>

                <BaseButton color="muted" @click="toggleDrawer">
                  {{ t('general.cancel') || 'Cancel' }}
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Vertical "Filter Report" text instead of Icon -->
    <div
      class="dark:bg-muted-800 fixed right-0 top-1/4 z-40 flex -translate-y-1/2 rounded-l-md bg-white px-2 py-4 shadow-lg"
    >
      <div
        class="hover:bg-muted-100 dark:hover:bg-muted-700 cursor-pointer transition-colors"
        :class="{ 'bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-600 text-white': isDrawerOpen }"
        @click="toggleDrawer"
      >
        <div class="vertical-text font-medium">
          Filter Report
        </div>
      </div>
    </div>

    <UiBusy v-if="delayedBusy && busy" />
  </div>
</template>

<script lang="ts" setup>

definePageMeta({
  layout: 'none',
})

import type { VisitType, VisitUserType } from '@safetynet/roci-api-bookings'
import reportTreeViewData, { reports, type IReport, type IReportFilter } from './reports'
import { TabulatorFull as Tabulator } from 'tabulator-tables'
import 'tabulator-tables/dist/css/tabulator.min.css'
import jsPDF from 'jspdf'
import { applyPlugin } from 'jspdf-autotable'
import BaseInputCaps from '~/components/ui/BaseInputCaps.vue'
import type { TreeItem } from '~/components/ui/locationSelector.vue'
import type { Location } from '@safetynet/roci-api-locations'
import { getAllCommonAncestors, isInSelectedLocations, getTopLevelLocations, fullLocationListFromTopLevel, HierarchicalDirection } from '~/pages/utils/locations'
import LocationSelector from '~/components/ui/locationSelector.vue'
import { checkbox, select, text } from '@shuriken-ui/tailwind/plugins'
import type { IssuableItemCategory, User } from '@safetynet/roci-api-users'
import { DateTime } from 'luxon'
import type { CustomFieldType } from '@safetynet/roci-api-custom-field-types'

applyPlugin(jsPDF)

const { $dataService } = useNuxtApp()
const { t } = useI18n()
const selectedReport = ref<IReport | undefined>()
const treeSelectRef = ref<any>(null)
const userVisitTypes = await $dataService.findVisitUsersTypesByTenant({})
const userCustomFields = await $dataService.findCustomFieldTypesByTenant()
const userCustomExpiryFields = userCustomFields.filter(a => a.dataType == 7 && a.isEnabled && (a.fieldType == 1 || a.fieldType == 2))
const selectedCustomExpiryFields = ref<CustomFieldType[]>([])
const selectedCustomFields = ref<CustomFieldType[]>([])
const selectedVisitTypes = ref<VisitUserType[]>([])
const table = ref<Tabulator | null>(null)
const locationModalOpen = ref(false)
const allLocations = ref<Location[]>(await $dataService.findLocationsByTenant({}))
const selectedIssuableItemCategories = ref<IssuableItemCategory[]>([])
const IssuableItemCategories = ref<IssuableItemCategory[]>(await $dataService.findIssuableItemCategories({}))
// Side drawer related state
const isDrawerOpen = ref(false)
const activeTab = ref<string | null>(null)
const activeTabComponent = ref<any>(null)
const drawerTitle = ref('Drawer')
const textFilters = ref<any>()
const dateFilters = ref<any>()
const boolFilters = ref<any>()
const selectFilters = ref<any>()
const currentUserAuth = await $dataService.getCurrentUser()
const loggedInUser = ref<User>(await $dataService.findUserByTenant(currentUserAuth.user.id))
const currentUser = ref<User | null>(loggedInUser.value)

const userLocations = currentUser.value?.locations ? allLocations.value.filter(a => currentUser.value?.locations?.some(l => l.locationId === a.locationId)) : []
const selectedLocations = ref<Location[]>(userLocations)

// Define the tabs for the side drawer
const drawerTabs = [
  { id: 'settings', icon: 'lucide:settings', title: 'Settings', component: null },
  { id: 'info', icon: 'lucide:info', title: 'Report Info', component: null },
  { id: 'help', icon: 'lucide:help-circle', title: 'Help', component: null },
  { id: 'notes', icon: 'lucide:file-text', title: 'Notes', component: null },
]

// Function to toggle the drawer visibility
const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value

  // If closing the drawer, reset active tab
  if (!isDrawerOpen.value) {
    activeTab.value = null
  }
}

// Function to open a specific tab
const openTab = (tabId: string) => {
  const tab = drawerTabs.find(t => t.id === tabId)
  if (tab) {
    drawerTitle.value = tab.title
    activeTab.value = tabId
    activeTabComponent.value = tab.component

    // If drawer is not open, open it
    if (!isDrawerOpen.value) {
      isDrawerOpen.value = true
    }
  }
}

const applySectedLocations = (locations: TreeItem[]) => {
  const newSelectedLocations = locations.map(l => allLocations.value.find((ll: Location) => ll.description == l.item)!)
  selectedLocations.value = getTopLevelLocations(Array.from(new Map([...newSelectedLocations].map(item => [item.locationId, item])).values()), allLocations.value)
  locationModalOpen.value = false
  table.value?.clearData()
}

const fetchAndBuildLocationsTree = async () => {
  function buildLocationTree(locations: Location[]): TreeItem[] {
    const locationMap = new Map<number, TreeItem>()
    const rootItems: TreeItem[] = []

    // First, create a TreeItem for each Location and store it in a map by locationId
    locations.forEach((location) => {
      locationMap.set(location.locationId, { item: location.description, open: false })
    })

    // Now build the tree structure
    locations.forEach((location) => {
      const treeNode = locationMap.get(location.locationId)!

      if (location.parentLocationId === null) {
        // If no parent, it's a root node
        rootItems.push(treeNode)
      }
      else {
        // Otherwise, find the parent and add this node to its children
        const parentTreeNode = locationMap.get(location.parentLocationId)
        if (parentTreeNode) {
          parentTreeNode.open = true
          if (!parentTreeNode.children) parentTreeNode.children = []
          parentTreeNode.children.push(treeNode)
        }
      }
    })

    return rootItems // Return root nodes, as they contain the full tree structure
  }

  const locations = await $dataService.findLocationsByTenant({ isEnabled: true })
  return buildLocationTree(locations)
}

const locations = ref(await fetchAndBuildLocationsTree())

const getSelectedTreeLocations = computed(() => {
  var locs = fullLocationListFromTopLevel(allLocations.value, selectedLocations.value, HierarchicalDirection.Descendent)
  locs = locs.filter((a: Location) => !allLocations.value.find((b: Location) => b.parentLocationId == a.locationId))
  return locs.map((l: Location) => {
    return { item: l.description, open: false }
  })
})

const goHome = () => {
  window.location.href = document.referrer + '/1_2'
}

const itemSelected = () => {
  table.value?.destroy()
  table.value = null
  const focusedItem = document.activeElement?.querySelector('div > div')
  selectedReport.value = reports.find((item: IReport) => item.ReportName === focusedItem?.textContent)
  selectedVisitTypes.value = [...userVisitTypes]
  selectedCustomFields.value = [...userCustomFields]
  selectedCustomExpiryFields.value = [...userCustomExpiryFields]
  selectedIssuableItemCategories.value = [...IssuableItemCategories.value]
  textFilters.value = {}
  dateFilters.value = {}
  boolFilters.value = {}
  selectFilters.value = {}
  selectedReport.value!.Filters.filter(a => (a.type == 'textInput')).map(f => textFilters.value[f.fieldName] = '')
  selectedReport.value!.Filters.filter(a => (a.type == 'date')).map(f => dateFilters.value[f.fieldName] = new Date().toISOString().split('T')[0])
  selectedReport.value!.Filters.filter(a => (a.type == 'boolean')).map(f => boolFilters.value[f.fieldName] = (f.default ?? false))
  selectedReport.value!.Filters.filter(a => (a.type == 'select')).map(f => selectFilters.value[f.fieldName] = (f.default ?? ''))
  isDrawerOpen.value = true
}

const generateReport = async () => {
  if (!selectedReport.value) {
    return
  }
  busy.value = true
  try {
    const reportLocations = fullLocationListFromTopLevel(allLocations.value, selectedLocations.value, HierarchicalDirection.Descendent)
    const payload: any = { childLocations: reportLocations.map(l => l.locationId) }
    selectedReport.value.Filters.forEach((filter: IReportFilter) => {
      switch (filter.type) {
        case 'textInput': {
          payload[filter.fieldName] = textFilters.value[filter.fieldName]
          break
        }
        case 'customFieldTypes':
          payload[filter.fieldName] = selectedCustomFields.value.map(a => a.customFieldTypeId)
          break
        case 'customExpiryFieldTypes':
          payload[filter.fieldName] = selectedCustomExpiryFields.value.map(a => a.customFieldTypeId)
          break
        case 'visitUserTypes':
          payload[filter.fieldName] = selectedVisitTypes.value.map(a => a.visitUserTypeId)
          break
        case 'issuableItemCategory':
          payload[filter.fieldName] = selectedIssuableItemCategories.value.map(a => a.issuableItemCategoryId)
          break
        case 'date': {
          payload[filter.fieldName] = dateFilters.value[filter.fieldName]
          break
        }
        case 'boolean': {
          payload[filter.fieldName] = boolFilters.value[filter.fieldName]
          break
        }
        case 'select': {
          payload[filter.fieldName] = selectFilters.value[filter.fieldName]
          break
        }
      }
    })

    var data = await $dataService.executeReport(selectedReport.value?.ReportExecuteName, payload)
    table.value = new Tabulator('#reports-result', {
      // set height of table (in CSS or here), this enables the Virtual DOM and improves render speed dramatically (can be any valid css height value)
      data: data, // assign data to table
      columns: selectedReport.value.Columns,
      downloadConfig: {
        columnHeaders: true,
        columnGroups: false, // do not include column groups in column headers for downloaded table
        rowHeaders: false, // do not include row headers in downloaded table
        rowGroups: false, // do not include row groups in downloaded table
        columnCalcs: false, // do not include column calcs in downloaded table
        dataTree: false, // do not include data tree in downloaded table
      },
      downloadRowRange: 'all',
      // @ts-ignore
      dependencies: {
        jspdf: jsPDF,
        DateTime: DateTime,
      },
      printAsHtml: true,
      printRowRange: 'all',
      printConfig: {
      },
      autoResize: true,
      autoResizeColumns: true,
      layout: 'fitColumns',
    })
    toggleDrawer()
  }
  finally {
    busy.value = false
  }
}

const delayedBusy = ref(false)
const busy = ref(false)

</script>

<style scoped>
/* Add any additional styling for the drawer here */
.vertical-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  padding: 8px 0;
  letter-spacing: 1px;
}
</style>
